import { useState } from 'react';

interface AuthPageProps {
  mode: 'login' | 'register';
  onLogin: (email: string, password: string) => Promise<any>;
  onRegister: (username: string, email: string, password: string, isMature: boolean) => Promise<any>;
  onNavigate: (page: string) => void;
}

const GENRE_OPTIONS = [
  'Romance', 'Romantasy', 'Fantasy', 'Dark Fantasy', 'Dark Romance',
  'Paranormal', 'Werewolf', 'Urban Fantasy', 'Sci-Fi', 'Thriller',
  'Mystery', 'Horror', 'Action', 'Adventure', 'Drama',
  'Historical', 'Young Adult', 'New Adult', 'LGBTQ+', 'Comedy',
];

const ROLE_OPTIONS = [
  { value: 'reader', label: 'Reader', icon: '📖', desc: 'I want to discover and read stories' },
  { value: 'author', label: 'Author', icon: '✍️', desc: 'I want to write and publish stories' },
  { value: 'both', label: 'Both', icon: '✨', desc: 'I love reading and writing' },
];

export default function AuthPage({ mode, onLogin, onRegister, onNavigate }: AuthPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isMature, setIsMature] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Multi-step registration state
  const [step, setStep] = useState<1 | 2>(1);
  const [role, setRole] = useState<'reader' | 'author' | 'both' | ''>('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !email.trim() || !password.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setStep(2);
  };

  const handleFinalSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      // Store role & genre preferences in localStorage for post-registration use
      if (role) localStorage.setItem('sh_onboard_role', role);
      if (selectedGenres.length > 0) localStorage.setItem('sh_onboard_genres', JSON.stringify(selectedGenres));
      await onRegister(username, email, password, isMature);
      onNavigate('home');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onLogin(email, password);
      onNavigate('home');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // ─── LOGIN VIEW ──────────────────────────────────────────────────────────────
  if (mode === 'login') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="ScribeHaven" className="w-12 h-12 rounded-full mx-auto" />
            <h1 className="text-2xl font-bold mt-2">Welcome Back</h1>
            <p className="text-gray-400 mt-1">Sign in to continue your journey</p>
          </div>

          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 focus:border-ink-500 focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 focus:border-ink-500 focus:outline-none"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-800 text-red-400 rounded-lg px-4 py-2 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="text-center mt-2">
              <button
                type="button"
                onClick={() => onNavigate('forgot-password')}
                className="text-sm text-gray-400 hover:text-ink-400 transition-colors"
              >
                Forgot your password?
              </button>
            </div>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-950 text-gray-500">or continue with</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <a
              href="/api/auth/google"
              className="w-full flex items-center justify-center gap-3 py-3 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl font-medium transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </a>
            <a
              href="/api/auth/discord"
              className="w-full flex items-center justify-center gap-3 py-3 bg-[#5865F2] hover:bg-[#4752C4] rounded-xl font-medium transition-colors text-white"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
              Sign in with Discord
            </a>
          </div>

          <p className="text-center text-sm text-gray-400 mt-6">
            Don't have an account?{' '}
            <button onClick={() => onNavigate('register')} className="text-ink-400 hover:underline">Join Free</button>
          </p>
        </div>
      </div>
    );
  }

  // ─── REGISTER STEP 1: Account Details ────────────────────────────────────────
  if (step === 1) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-full bg-ink-600 text-white text-xs font-bold flex items-center justify-center">1</div>
              <span className="text-xs text-ink-400 font-medium">Account</span>
            </div>
            <div className="w-8 h-px bg-gray-700" />
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-full bg-gray-800 text-gray-500 text-xs font-bold flex items-center justify-center">2</div>
              <span className="text-xs text-gray-500">Preferences</span>
            </div>
          </div>

          <div className="text-center mb-8">
            <img src="/logo.png" alt="ScribeHaven" className="w-12 h-12 rounded-full mx-auto" />
            <h1 className="text-2xl font-bold mt-2">Join ScribeHaven</h1>
            <p className="text-gray-400 mt-1">Create your account to get started</p>
          </div>

          <form onSubmit={handleStep1Submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Username / Pen Name</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 focus:border-ink-500 focus:outline-none"
                placeholder="Your pen name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 focus:border-ink-500 focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 focus:border-ink-500 focus:outline-none"
                placeholder="At least 6 characters"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={isMature}
                onChange={e => setIsMature(e.target.checked)}
                className="rounded"
              />
              I am 18+ and want to see mature content
            </label>

            {error && (
              <div className="bg-red-900/20 border border-red-800 text-red-400 rounded-lg px-4 py-2 text-sm">{error}</div>
            )}

            <button
              type="submit"
              className="w-full py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
            >
              Continue →
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-950 text-gray-500">or sign up with</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <a
              href="/api/auth/google"
              className="w-full flex items-center justify-center gap-3 py-3 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl font-medium transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign up with Google
            </a>
            <a
              href="/api/auth/discord"
              className="w-full flex items-center justify-center gap-3 py-3 bg-[#5865F2] hover:bg-[#4752C4] rounded-xl font-medium transition-colors text-white"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
              Sign up with Discord
            </a>
          </div>

          <p className="text-center text-sm text-gray-400 mt-6">
            Already have an account?{' '}
            <button onClick={() => onNavigate('login')} className="text-ink-400 hover:underline">Sign In</button>
          </p>
        </div>
      </div>
    );
  }

  // ─── REGISTER STEP 2: Role + Genre Preferences ───────────────────────────────
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 rounded-full bg-ink-600/40 text-ink-400 text-xs font-bold flex items-center justify-center">✓</div>
            <span className="text-xs text-gray-500">Account</span>
          </div>
          <div className="w-8 h-px bg-gray-700" />
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 rounded-full bg-ink-600 text-white text-xs font-bold flex items-center justify-center">2</div>
            <span className="text-xs text-ink-400 font-medium">Preferences</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold">Personalize Your Experience</h1>
          <p className="text-gray-400 mt-1">Tell us a bit about yourself so we can tailor ScribeHaven for you</p>
        </div>

        {/* Role selection */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">I am primarily a...</h2>
          <div className="grid grid-cols-3 gap-3">
            {ROLE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRole(opt.value as any)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                  role === opt.value
                    ? 'border-ink-500 bg-ink-600/20 text-white'
                    : 'border-gray-800 bg-gray-900 text-gray-400 hover:border-gray-600'
                }`}
              >
                <span className="text-2xl">{opt.icon}</span>
                <span className="text-sm font-semibold">{opt.label}</span>
                <span className="text-xs opacity-70 leading-tight">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Genre preferences */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-1">
            What genres do you love?
          </h2>
          <p className="text-xs text-gray-500 mb-3">Select all that apply — we'll use this to recommend stories</p>
          <div className="flex flex-wrap gap-2">
            {GENRE_OPTIONS.map(genre => (
              <button
                key={genre}
                type="button"
                onClick={() => toggleGenre(genre)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedGenres.includes(genre)
                    ? 'bg-ink-600 text-white'
                    : 'bg-gray-900 text-gray-400 border border-gray-800 hover:border-ink-500/50'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800 text-red-400 rounded-lg px-4 py-2 text-sm mb-4">{error}</div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="px-5 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl font-medium transition-colors text-gray-300"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleFinalSubmit}
            disabled={loading}
            className="flex-1 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating account...' : 'Create Account & Start Reading'}
          </button>
        </div>

        <p className="text-center text-xs text-gray-500 mt-4">
          You can skip this step —{' '}
          <button
            type="button"
            onClick={handleFinalSubmit}
            disabled={loading}
            className="text-ink-400 hover:underline"
          >
            skip for now
          </button>
        </p>
      </div>
    </div>
  );
}
